import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loginAdmin, getMeAdmin } from '../api';
import { setPendingCustomerToken } from '../api/client';
import { getToken, setToken, removeToken, clearAllAuth, COOKIE_KEYS } from '../utils/cookies';
import { normalizeAdminRoleCode } from '../utils/adminPermissions';

export const ADMIN_LOGIN_PATH = '/admin/login';
export const ADMIN_CHANGE_PASSWORD_PATH = '/admin/cambiar-contrasena';

const SecurityContext = createContext();

export const useSecurity = () => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error('useSecurity debe usarse dentro de SecurityProvider');
  }
  return context;
};

function mapAdminUser(data) {
  return {
    id: data.id,
    username: data.email,
    displayName: data.full_name,
    full_name: data.full_name,
    email: data.email,
    role: normalizeAdminRoleCode(data.role ?? data.role_code) || 'viewer',
    must_change_password: Boolean(data.must_change_password),
  };
}

export const SecurityProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const bootstrapGenRef = useRef(0);

  const handleLogout = useCallback(() => {
    setIsAuthenticated(false);
    setAdminUser(null);
    removeToken(COOKIE_KEYS.ADMIN_TOKEN);
    if (navigate) {
      navigate(ADMIN_LOGIN_PATH, { replace: true });
    }
  }, [navigate]);

  const bootstrapAdminSession = useCallback(async () => {
    const gen = ++bootstrapGenRef.current;
    const token = getToken(COOKIE_KEYS.ADMIN_TOKEN);

    if (!token) {
      setIsAuthenticated(false);
      setAdminUser(null);
      setIsInitializing(false);
      return;
    }

    try {
      const { data } = await getMeAdmin();
      if (gen !== bootstrapGenRef.current) return;
      setIsAuthenticated(true);
      setAdminUser(mapAdminUser(data));
    } catch {
      if (gen !== bootstrapGenRef.current) return;
      removeToken(COOKIE_KEYS.ADMIN_TOKEN);
      setIsAuthenticated(false);
      setAdminUser(null);
    } finally {
      if (gen === bootstrapGenRef.current) {
        setIsInitializing(false);
      }
    }
  }, []);

  /** Restaura sesión admin una sola vez al montar (no en cada cambio de ruta). */
  useEffect(() => {
    bootstrapAdminSession();
  }, [bootstrapAdminSession]);

  /** En login admin no mezclar token de cliente ni legacy. */
  useEffect(() => {
    if (location.pathname === ADMIN_LOGIN_PATH) {
      clearAllAuth();
      setPendingCustomerToken(null);
    }
  }, [location.pathname]);

  /** Guard de rutas: solo redirige si no hay token; no revalida /me en cada navegación. */
  useEffect(() => {
    if (isInitializing) return;
    const isAdminRoute =
      location.pathname.startsWith('/admin') && location.pathname !== ADMIN_LOGIN_PATH;
    if (!isAdminRoute) return;

    const token = getToken(COOKIE_KEYS.ADMIN_TOKEN);
    if (!token) {
      handleLogout();
    }
  }, [location.pathname, isInitializing, handleLogout]);

  /** Primer ingreso: obligar cambio de contraseña temporal. */
  useEffect(() => {
    if (isInitializing || !isAuthenticated || !adminUser?.must_change_password) return;
    const path = location.pathname;
    const allowed = path === ADMIN_LOGIN_PATH || path === ADMIN_CHANGE_PASSWORD_PATH;
    if (path.startsWith('/admin') && !allowed) {
      navigate(ADMIN_CHANGE_PASSWORD_PATH, { replace: true });
    }
  }, [
    isInitializing,
    isAuthenticated,
    adminUser?.must_change_password,
    location.pathname,
    navigate,
  ]);

  const handleLogin = useCallback(async (username, password) => {
    const email = username.includes('@') ? username : `${username}@dentalpro.com`;
    clearAllAuth();
    setPendingCustomerToken(null);
    const gen = ++bootstrapGenRef.current;
    try {
      const { data } = await loginAdmin(email, password);
      setToken(COOKIE_KEYS.ADMIN_TOKEN, data.access_token);
      const { data: me } = await getMeAdmin();
      if (gen !== bootstrapGenRef.current) {
        return { success: false, error: 'Inicio de sesión cancelado. Intentá de nuevo.' };
      }
      setAdminUser(mapAdminUser(me));
      setIsAuthenticated(true);
      setIsInitializing(false);
      return {
        success: true,
        token: data.access_token,
        mustChangePassword: Boolean(me.must_change_password),
      };
    } catch (err) {
      if (gen === bootstrapGenRef.current) {
        removeToken(COOKIE_KEYS.ADMIN_TOKEN);
        setIsAuthenticated(false);
        setAdminUser(null);
      }
      const msg = err.response?.data?.detail || 'Credenciales inválidas';
      return {
        success: false,
        error: typeof msg === 'string' ? msg : msg[0]?.msg || 'Credenciales inválidas',
      };
    }
  }, []);

  const requireAuth = useCallback((callback) => {
    if (!isAuthenticated) {
      if (navigate) {
        navigate(ADMIN_LOGIN_PATH, { replace: true });
      }
      return false;
    }
    return callback();
  }, [isAuthenticated, navigate]);

  const value = {
    isAuthenticated,
    isInitializing,
    sessionToken: getToken(COOKIE_KEYS.ADMIN_TOKEN),
    adminUser,
    handleLogin,
    handleLogout,
    requireAuth,
    refreshAdminSession: bootstrapAdminSession,
  };

  return <SecurityContext.Provider value={value}>{children}</SecurityContext.Provider>;
};
